// ... (imports unchanged)
import React, { useEffect, useState } from 'react';
import { db } from '../firebase/firebaseConfig';
import { collection, getDocs } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid, LineChart, Line, Legend, PieChart, Pie, Cell
} from 'recharts';
import { useNavigate } from 'react-router-dom';

const ClientDashboard = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [lostItems, setLostItems] = useState([]);
  const [foundItems, setFoundItems] = useState([]);
  const [claims, setClaims] = useState([]);

  useEffect(() => {
    if (!currentUser) return;

    const fetchData = async () => {
      const [lostSnap, foundSnap, claimsSnap] = await Promise.all([
        getDocs(collection(db, 'lostItems')),
        getDocs(collection(db, 'foundItems')),
        getDocs(collection(db, 'claims')),
      ]);

      const userEmail = currentUser.email?.toLowerCase();
      const userName = currentUser.displayName?.toLowerCase() || '';

      const matchUser = (item) => {
        const email =
          item.userEmail?.toLowerCase() ||
          item.email?.toLowerCase() ||
          '';
        const name =
          item.yourName?.toLowerCase() ||
          item.finderName?.toLowerCase() ||
          '';
        return email === userEmail || name === userName;
      };

      const mapWithDates = (docs) =>
        docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));

      const lost = mapWithDates(lostSnap.docs).filter(matchUser);
      const found = mapWithDates(foundSnap.docs).filter(matchUser);
      const claimed = mapWithDates(claimsSnap.docs).filter(matchUser);

      setLostItems(lost);
      setFoundItems(found);
      setClaims(claimed);
    };

    fetchData();
  }, [currentUser]);

  const chartData = [
    { name: 'Lost Items', count: lostItems.length },
    { name: 'Found Items', count: foundItems.length },
    { name: 'Claims', count: claims.length },
  ];

  const pieData = chartData.map((item) => ({
    name: item.name,
    value: item.count
  }));

  const COLORS = ['#e74c3c', '#2ecc71', '#3498db'];

  const formatDate = (rawDate) => {
    if (!rawDate) return 'Not provided';
    try {
      let dateObj;
      if (typeof rawDate === 'string') {
        dateObj = new Date(rawDate);
      } else if (rawDate.toDate) {
        dateObj = rawDate.toDate();
      } else if (rawDate.seconds) {
        dateObj = new Date(rawDate.seconds * 1000);
      } else {
        dateObj = new Date(rawDate);
      }
      return isNaN(dateObj.getTime()) ? 'Not provided' : dateObj.toLocaleDateString();
    } catch {
      return 'Not provided';
    }
  };

  return (
    <div style={{ padding: '2rem' }}>
      <h2 style={{ fontSize: '24px', marginBottom: '1.5rem' }}>Welcome, {currentUser?.displayName || 'User'}</h2>

      {/* Bar and Line charts */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1.5rem' }}>
        <div style={chartStyle}>
          <h3 style={chartTitle}>Your Activity Summary (Bar)</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart layout="vertical" data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" allowDecimals={false} />
              <YAxis dataKey="name" type="category" />
              <Tooltip />
              <Bar dataKey="count">
                {chartData.map((entry, index) => (
                  <Cell key={`bar-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div style={chartStyle}>
          <h3 style={chartTitle}>Trends Overview (Line)</h3>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="count" stroke="#10b981" strokeWidth={3} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Pie Chart + Table SIDE BY SIDE */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem', marginTop: '2rem', alignItems: 'flex-start' }}>
        <div style={{ ...chartStyle, flex: '1 1 350px', minWidth: '300px' }}>
          <h3 style={chartTitle}>Distribution of Reports (Pie)</h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
                label
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div style={{ ...tableContainerStyle, flex: '2 1 600px', minWidth: '700px' }}>
          <h3 style={{ marginBottom: '1rem', fontSize: '1.3rem', fontWeight: '600' }}>📋 All Activities</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#e5e7eb', textAlign: 'left' }}>
                <th style={thStyle}>Type</th>
                <th style={thStyle}>Image</th>
                <th style={thStyle}>Item Name</th>
                <th style={thStyle}>Description</th>
                <th style={thStyle}>Date</th>
                <th style={thStyle}>Location</th>
              </tr>
            </thead>
            <tbody>
              {lostItems.map(item => (
                <tr key={`lost-${item.id}`} style={trStyle}>
                  <td style={tdStyle}>🔴 Lost</td>
                  <td style={tdStyle}>{item.imageUrl ? <img src={item.imageUrl} alt="Lost" style={imageStyle} /> : '—'}</td>
                  <td style={tdStyle}>{item.itemName || 'Unnamed'}</td>
                  <td style={tdStyle}>{item.description || 'No description'}</td>
                  <td style={tdStyle}>{formatDate(item.dateLost || item.createdAt)}</td>
                  <td style={tdStyle}>{item.location || 'Not provided'}</td>
                </tr>
              ))}
              {foundItems.map(item => (
                <tr key={`found-${item.id}`} style={trStyle}>
                  <td style={tdStyle}>🟢 Found</td>
                  <td style={tdStyle}>{item.imageUrl ? <img src={item.imageUrl} alt="Found" style={imageStyle} /> : '—'}</td>
                  <td style={tdStyle}>{item.itemName || 'Unnamed'}</td>
                  <td style={tdStyle}>{item.description || 'No description'}</td>
                  <td style={tdStyle}>{formatDate(item.dateFound || item.createdAt)}</td>
                  <td style={tdStyle}>{item.location || 'Not provided'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ textAlign: 'center', marginTop: '2.5rem' }}>
        <button style={buttonStyle} onClick={() => navigate('/report-lost')}>
          Report Lost/Found Item
        </button>
      </div>

      <p style={{ textAlign: 'center', marginTop: '3rem', fontSize: '14px', color: '#888' }}>
        © 2025 USIU-Africa Asset Recovery System. All rights reserved.
      </p>
    </div>
  );
};

// STYLES
const chartStyle = {
  width: '100%',
  backgroundColor: 'white',
  borderRadius: '8px',
  padding: '1rem',
  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
};

const chartTitle = {
  textAlign: 'center',
  marginBottom: '1rem',
  fontSize: '18px',
  fontWeight: 'bold',
  color: '#374151',
};

const tableContainerStyle = {
  backgroundColor: '#f9f9f9',
  borderRadius: '12px',
  padding: '1.5rem',
  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
  overflowX: 'auto',
};

const thStyle = {
  padding: '10px 16px',
  fontWeight: '600',
  fontSize: '14px',
  borderBottom: '2px solid #ddd',
};

const tdStyle = {
  padding: '12px 16px',
  borderBottom: '1px solid #eee',
  fontSize: '14px',
};

const trStyle = {
  backgroundColor: '#fff',
};

const imageStyle = {
  width: '50px',
  height: '50px',
  objectFit: 'cover',
  borderRadius: '8px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
};

const buttonStyle = {
  backgroundColor: '#4f46e5',
  color: 'white',
  padding: '0.75rem 1.5rem',
  border: 'none',
  borderRadius: '9999px',
  cursor: 'pointer',
  fontSize: '16px',
};

export default ClientDashboard;
